import http from '../utils/http';
import { interpolate } from 'utils/string';
import type {
  BulkUpdateLeaveIssuerChangeRequestResponse,
  ChangeIssuer,
  LeaveIssuerChangeRequestListResponse,
} from 'interface/issuer';
import type { ServiceBody, ServiceId, ServiceParams, ServicePayload } from 'interface/service';

import {
  LEAVE_ISSUER_CHANGE_REQUEST,
  LEAVE_ISSUER_CHANGE_REQUESTS_LIST,
  UPDATE_LEAVE_ISSUER_CHANGE_REQUEST,
  BULK_UPDATE_LEAVE_ISSUER_CHANGE_REQUEST,
  LEAVE_ISSUER_CHANGE_REQUESTS_LIST_EXPORT,
} from '../constants/endpoints';

/**
 * Fetch all leave issuer change requests.
 *
 * @param {Object} params
 * @returns {Promise}
 */
export async function fetchAll(params: ServiceParams) {
  return await http.get<LeaveIssuerChangeRequestListResponse>(LEAVE_ISSUER_CHANGE_REQUESTS_LIST, {
    params: { ...params },
  });
}

/**
 * Gets a list of leave issuer change requests as a CSV.
 *
 * @param {Object} params An object containing the query parameters for the export request.
 * @returns Raw axios response whose data is the exported Blob.
 */
export async function exportAll(params: ServiceParams) {
  return await http.get<Blob>(
    LEAVE_ISSUER_CHANGE_REQUESTS_LIST_EXPORT,
    { params: { ...params }, responseType: 'blob' },
    true
  );
}

/**
 * Update status of individual’s leave issuer change request.
 *
 * @param {Number} userId
 * @param {Object} updatedRequest
 * @returns {Promise}
 */
export async function updateLeaveIssuerChangeRequest(userId: ServiceId, updatedRequest: ServicePayload) {
  return await http.patch(interpolate(UPDATE_LEAVE_ISSUER_CHANGE_REQUEST, { userId }), {
    body: updatedRequest,
  });
}

/**
 * Update status of multiple leave issuer change requests.
 *
 * @param {Object} updatedRequests
 * @returns {Promise}
 */
export async function bulkUpdateLeaveIssuerChangeRequest(body: ServiceBody) {
  return await http.post<BulkUpdateLeaveIssuerChangeRequestResponse>(BULK_UPDATE_LEAVE_ISSUER_CHANGE_REQUEST, {
    body,
  });
}

/**
 * Fetch leave issuer change request by user id.
 *
 * @param {number} userId
 * @returns {Promise}
 */
export async function fetchLeaveIssuerChangeRequestByUserId(userId: ServiceId) {
  return await http.get(interpolate(LEAVE_ISSUER_CHANGE_REQUEST, { userId }));
}

/**
 * Create leave issuer change request.
 *
 * @param {number} userId
 * @param {object} data
 * @returns {Promise}
 */
export async function createLeaveIssuerChangeRequest(userId: ServiceId, data: ChangeIssuer) {
  return await http.post(interpolate(LEAVE_ISSUER_CHANGE_REQUEST, { userId }), { body: data });
}
